import React, { useState } from 'react';
import './AssignMultiple.css';
import CloseIcon from '@mui/icons-material/Close';
import { Button, Checkbox } from '@mui/material';
import { useGetAllUsersQuery } from '../../../../../redux-toolkit/features/company/companyApiSlice';
import ProgressBar from '../../../../UI/ProgressBar';
import { Error404 } from '../../../../UI/404/Error404';
import { useAssignMultipleRolesMutation } from '../../../../../redux-toolkit/features/employee/employeeApiSlice';
import { useAlert } from '../../../../../hooks/useAlert';

const ADMIN_ROLE_ID = '6359025cf0f19b14f64673d9';
const SUPERADMIN_ROLE_ID = '63593a15dcfcbad0acd22697';

export const AssignMultiple = ({
  selectedEmployee,
  setSelectedEmployee,
  setOpenAssignRole,
  handleAssignModalClose,
  canAssignSuperAdmin = true,
  superAdminRoleId = SUPERADMIN_ROLE_ID,
}) => {
  const [active, setActive] = useState(ADMIN_ROLE_ID);
  const [check, setCheck] = useState(false);
  const [grantPromotionAccess, setGrantPromotionAccess] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const { data, isLoading, isError } = useGetAllUsersQuery({
    sortBy: 'createdAt',
    type: 'desc',
    query: '',
    limit: 1000,
    page: 1,
  });
  const [assignMultipleRoles] = useAssignMultipleRolesMutation();
  const { displayAlert } = useAlert();

  const selectedUsers = (data?.data?.users?.list || []).filter((item) =>
    selectedEmployee?.includes(item._id)
  );

  const allHavePromotionAccess =
    selectedUsers.length > 0 &&
    selectedUsers.every((u) => u.promotionAccess === true);
  const mixedCohort =
    selectedUsers.length > 1 &&
    selectedUsers.some((u) => u.promotionAccess === true) &&
    !allHavePromotionAccess;

  const isSuperAdminSelected = active === superAdminRoleId;
  const selectedCount = selectedEmployee.length;

  const removeEmployeeHandler = (_id) => {
    setSelectedEmployee(selectedEmployee.filter((item) => item !== _id));
  };

  const selectAdmin = () => {
    setActive(ADMIN_ROLE_ID);
    setGrantPromotionAccess(false);
  };

  const selectSuperAdmin = () => {
    if (!isSuperAdminSelected) {
      setGrantPromotionAccess(allHavePromotionAccess);
    }
    setActive(superAdminRoleId);
  };

  const assignRoles = async () => {
    const finalData = {
      userIds: selectedEmployee,
      permissionSetId: active,
      ...(isSuperAdminSelected
        ? { grantPromotionAccess: !!grantPromotionAccess }
        : {}),
    };
    setConfirming(true);
    try {
      await assignMultipleRoles(finalData).unwrap();
      displayAlert({
        open: true,
        message: 'Successfully Updated Roles',
        type: 'success',
        timeout: '2000',
      });
      setSelectedEmployee([]);
      setOpenAssignRole(false);
    } catch (err) {
      displayAlert({
        open: true,
        message: `${err?.data?.message || err}`,
        type: 'error',
        timeout: '2000',
      });
    } finally {
      setConfirming(false);
    }
  };

  if (isLoading) {
    return <ProgressBar />;
  }

  if (isError) {
    return <Error404 />;
  }

  return (
    <div className="assignmultiple">
      <div className="assignmultiple__users">
        {selectedCount > 0 ? (
          selectedUsers.map((elm) => (
            <div
              key={elm._id}
              className="assignmultiple__userData">
              <div>
                <h5>{`${elm.fname || ''} ${elm.lname || ''}`.trim()}</h5>
                <h6 className="ftwt__r">{elm.email}</h6>
              </div>
              <CloseIcon
                onClick={() => removeEmployeeHandler(elm._id)}
                sx={{
                  fontSize: '18px',
                  cursor: 'pointer',
                  color: 'var(--grey400)',
                  '&:hover': {
                    color: 'var(--error)',
                  },
                }}
              />
            </div>
          ))
        ) : (
          <h4 style={{ textAlign: 'center' }}>
            Select atleast one user to assign role
          </h4>
        )}
      </div>
      <div>
        <div
          onClick={selectAdmin}
          className={
            active === ADMIN_ROLE_ID
              ? 'assignmultiple__roleActive'
              : 'assignmultiple__role'
          }>
          <h6>Admin</h6>
        </div>
        {canAssignSuperAdmin && (
          <div
            onClick={selectSuperAdmin}
            className={`${
              isSuperAdminSelected
                ? 'assignmultiple__roleActive'
                : 'assignmultiple__role'
            }${isSuperAdminSelected ? ' assignmultiple__role--expanded' : ''}`}>
            <h6>Super Admin</h6>
            {isSuperAdminSelected && (
              <label
                className="assignmultiple__promotion"
                onClick={(event) => event.stopPropagation()}>
                <Checkbox
                  checked={grantPromotionAccess}
                  onChange={() => setGrantPromotionAccess((value) => !value)}
                  disabled={confirming}
                  sx={{
                    p: 0,
                    mt: '1px',
                    '& .MuiSvgIcon-root': { fontSize: '20px' },
                  }}
                />
                <span className="assignmultiple__promotion-copy">
                  <span className="assignmultiple__promotion-title">
                    Grant Promotion Permission
                  </span>
                  <span className="assignmultiple__promotion-desc">
                    Enables the user to access promotion permissions.
                  </span>
                  {mixedCohort && (
                    <span className="assignmultiple__promotion-hint">
                      Applies to all selected users. Leaving this unchecked will
                      revoke Promotion access for anyone who already has it.
                    </span>
                  )}
                </span>
              </label>
            )}
          </div>
        )}
      </div>
      <div className="assignmultiple__check">
        <Checkbox
          checked={check}
          onChange={() => setCheck(!check)}
          sx={{ '& .MuiSvgIcon-root': { fontSize: '20px' } }}
        />
        <div>
          <h6 className="ftwt__m">
            Confirm the Role Change of "{selectedCount}" users
          </h6>
          <h6 className="ftwt__r assignmultiple__small">
            Changing role enables the user to access permissions assigned under
            that category.
          </h6>
        </div>
      </div>

      <div className="assignmultiple__actions">
        <Button
          onClick={handleAssignModalClose}
          variant="outlined">
          Cancel
        </Button>
        <Button
          disabled={!check || selectedCount === 0 || confirming}
          variant="contained"
          onClick={assignRoles}>
          {confirming ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
};
